/**
 * uc-core auth endpoints (src/modules/auth/auth.controller.ts — no global prefix):
 *   POST /auth/login    { email, password }   → AuthResponse   (email field also accepts username/phone)
 *   POST /auth/refresh  { refreshToken }      → AuthResponse   (refresh token rotates)
 *   POST /auth/logout   { refreshToken }      → 204
 *   POST /auth/me                             → profile with roles [{ id, name }]
 */
import { z } from 'zod'
import { api, tokenStore } from '@/api'
import { toTokenPair } from '@/api/client'
import { encryptPassword } from './passwordCipher'

export const authResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  tokenType: z.string().optional(),
  user: z.looseObject({
    id: z.string(),
    username: z.string(),
    email: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    status: z.string().optional(),
  }),
})
export type AuthResponse = z.infer<typeof authResponseSchema>

export const currentUserSchema = z.looseObject({
  id: z.string(),
  username: z.string(),
  email: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  fullName: z.string().nullish(),
  status: z.string().optional(),
  contactNo: z.string().nullish(),
  profileImageUrl: z.string().nullish(),
  roles: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
  /**
   * `resource:action` strings from GET /auth/permissions, merged in by fetchProfile(). Undefined
   * when that endpoint is not available (older uc-core): the console then allows every action and
   * relies on the server's 403.
   */
  permissions: z.array(z.string()).optional(),
  preferences: z.record(z.string(), z.unknown()).nullish(),
})
export type CurrentUser = z.infer<typeof currentUserSchema>

export async function login(identifier: string, password: string): Promise<AuthResponse> {
  const encrypted = await encryptPassword(password)
  // passport-local reads the `email` field; uc-core matches it against username OR email.
  const res = await api.post(
    '/auth/login',
    { email: identifier.trim(), password: encrypted },
    {
      schema: authResponseSchema,
      anonymous: true,
    },
  )
  tokenStore.set(toTokenPair(res))
  return res
}

export async function fetchMe(): Promise<CurrentUser> {
  return api.post('/auth/me', undefined, { schema: currentUserSchema })
}

export const permissionsResponseSchema = z.object({
  roles: z.array(z.string()).default([]),
  permissions: z.array(z.string()).default([]),
})
export type PermissionsResponse = z.infer<typeof permissionsResponseSchema>

/** GET /auth/permissions → `{ roles, permissions }` (uc-core F4.1-p). */
export async function fetchPermissions(): Promise<PermissionsResponse> {
  return api.get('/auth/permissions', { schema: permissionsResponseSchema })
}

/**
 * Profile + permissions. A failing permissions call (404 on an older backend, a blip) does not
 * fail the sign-in: the user simply has no `permissions` list and actions fall back to "allowed".
 */
export async function fetchProfile(): Promise<CurrentUser> {
  const [me, perms] = await Promise.all([
    fetchMe(),
    fetchPermissions().catch(() => null),
  ])
  return perms ? { ...me, permissions: perms.permissions } : me
}

export async function logout(): Promise<void> {
  const tokens = tokenStore.get()
  try {
    if (tokens) await api.post('/auth/logout', { refreshToken: tokens.refreshToken })
  } catch {
    /* offline or already invalid — clearing locally is what matters */
  } finally {
    tokenStore.clear()
  }
}

export function displayName(
  u: Pick<CurrentUser, 'fullName' | 'firstName' | 'lastName' | 'username'>,
): string {
  return u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username
}
